import { useEffect, useState } from "react";
import Editor, { useMonaco } from "@monaco-editor/react";
import { useMantineColorScheme, Modal, ActionIcon, Tooltip, Box } from "@mantine/core";
import { TableSchema } from "@bi-studio/shared";
import { Maximize2 } from "lucide-react";

interface SqlEditorProps {
    value: string;
    onChange: (value: string | undefined) => void;
    height?: string | number;
    readOnly?: boolean;
    schema?: TableSchema[];
}


const PREDEFINED_VARIABLES = [
    { label: "{{_user_id}}", detail: "Current User ID" },
    { label: "{{_company_id}}", detail: "Current Company ID" },
    { label: "{{variable_name}}", detail: "Custom Variable Template" },
];

export function SqlEditor({ value, onChange, height = "400px", readOnly = false, schema = [] }: SqlEditorProps) {
    const { colorScheme } = useMantineColorScheme();
    const [fullScreen, setFullScreen] = useState(false);
    const monaco = useMonaco(); // Get monaco instance

    useEffect(() => {
        if (!monaco) return;

        // Register SQL completion provider
        // This effect runs once when monaco is available, and re-runs if schema changes.
        // It returns a cleanup function to dispose the provider, preventing duplicates.
        const disposable = monaco.languages.registerCompletionItemProvider("sql", {
            provideCompletionItems: (model: any, position: any) => {
                const word = model.getWordUntilPosition(position);
                const range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: word.endColumn,
                };

                // Use the current schema directly from props, as it's a dependency of this effect
                const currentSchema = schema || [];

                const tableSuggestions = currentSchema.map(table => ({
                    label: table.name,
                    kind: monaco.languages.CompletionItemKind.Class,
                    insertText: table.name,
                    detail: `Table (${table.schema})`,
                    range,
                }));

                const columnSuggestions = currentSchema.flatMap(table =>
                    table.columns.map(column => ({
                        label: column,
                        kind: monaco.languages.CompletionItemKind.Field,
                        insertText: column,
                        detail: `Column (${table.name})`,
                        range,
                    }))
                );

                const suggestions = [
                    // Tables
                    ...tableSuggestions,
                    // Columns
                    ...columnSuggestions,
                    // Variables
                    ...PREDEFINED_VARIABLES.map(variable => ({
                        label: variable.label,
                        kind: monaco.languages.CompletionItemKind.Variable,
                        insertText: variable.label,
                        detail: variable.detail,
                        range,
                    })),
                    // Trigger variable suggestion with {{
                    {
                        label: "{{",
                        kind: monaco.languages.CompletionItemKind.Snippet,
                        insertText: "{{${1:variable_name}}}",
                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                        detail: "Variable Template",
                        range,
                    }
                ];

                return { suggestions };
            }
        });

        return () => {
            disposable.dispose();
        };
    }, [monaco, schema]); // Re-register provider if monaco instance or schema changes

    // Common editor options
    const editorOptions = {
        minimap: { enabled: false },
        fontSize: 14,
        padding: { top: 16 },
        readOnly,
        suggest: {
            showKeywords: true,
        },
        scrollBeyondLastLine: false, // Prevent extra scroll space at the bottom
    };

    return (
        <>
            {/* Main Editor */}
            <Box pos="relative" h={height} className="border rounded-md overflow-hidden">
                <Editor
                    height="100%"
                    defaultLanguage="sql"
                    theme={colorScheme === 'dark' ? "vs-dark" : "light"}
                    value={value}
                    onChange={onChange}
                    options={editorOptions}
                />
                <Tooltip label="Fullscreen">
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        pos="absolute"
                        top={8}
                        right={16} // Adjusted to account for potential scrollbar or padding
                        style={{ zIndex: 10 }}
                        onClick={() => setFullScreen(true)}
                    >
                        <Maximize2 size={16} />
                    </ActionIcon>
                </Tooltip>
            </Box>

            {/* Fullscreen Modal Editor */}
            <Modal
                opened={fullScreen}
                onClose={() => setFullScreen(false)}
                fullScreen
                title="SQL Editor"
                overlayProps={{
                    backgroundOpacity: 0.1,
                    blur: 2,
                }}
                styles={{
                    content: {
                        backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'white',
                    },
                    header: {
                        backgroundColor: 'transparent',
                        borderBottom: '1px solid var(--modal-border)',
                    },
                    body: {
                        padding: 0,
                        height: 'calc(100vh - 60px)',
                    }
                }}
            >
                <Box h="100%" w="100%">
                    <Editor
                        height="100%"
                        defaultLanguage="sql"
                        theme={colorScheme === 'dark' ? "vs-dark" : "light"}
                        value={value}
                        onChange={onChange}
                        options={{ ...editorOptions, minimap: { enabled: true } }}
                    />
                </Box>
            </Modal>
        </>
    );
}
